import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "buttonLabel", "commentField", "loader", "success", "runningBadge"]
  static values = { generateUrl: String, statusUrl: String, initialStatus: String }

  connect() {
    this._inFlight = false
    this._pollTimer = null
    this._pollAttempts = 0

    // Backoff / timeout
    this._pollDelayMs = 1200
    this._elapsedMs = 0
    this._maxTotalMs = 10 * 60 * 1000 // 10 minutes

    // Reprise après reload / retour page si une génération est déjà en cours
    if (this.hasInitialStatusValue && this.initialStatusValue === "running") {
      this._inFlight = true

      // UI en mode "en cours"
      this._setLoading(true)

      // Badge au-dessus du champ (si présent dans la vue)
      if (this.hasRunningBadgeTarget) {
        this.runningBadgeTarget.classList.remove("d-none")
      }

      // Relance le polling (sans relancer le job)
      this._startPolling()
    }
  }

  disconnect() {
    this._stopPolling()
  }

  async generate() {
    if (this._inFlight) return
    this._inFlight = true
    this._setLoading(true)

    try {
      const resp = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json"
        }
      })

      if (!resp.ok) throw new Error(await resp.text())
      await resp.json()

      // Reset polling state
      this._pollAttempts = 0
      this._pollDelayMs = 1200
      this._elapsedMs = 0

      this._startPolling()
    } catch (e) {
      console.error(e)
      alert("Impossible de lancer la génération. Vérifie les logs.")
      this._setLoading(false)
      this._inFlight = false
    }
  }

  _startPolling() {
    this._stopPolling()
    this._scheduleNextPoll(0) // poll immédiat
  }

  _scheduleNextPoll(delayMs = this._pollDelayMs) {
    this._stopPolling()
    this._pollTimer = setTimeout(() => this._pollOnce(), delayMs)
  }

  _stopPolling() {
    if (this._pollTimer) clearTimeout(this._pollTimer)
    this._pollTimer = null
  }

  async _pollOnce() {
    this._pollAttempts += 1
    this._elapsedMs += this._pollDelayMs

    // Si ça dépasse, on arrête de poller mais on ne “fail” pas
    if (this._elapsedMs > this._maxTotalMs) {
      this._stopPolling()

      alert("La génération est toujours en cours. Tu peux laisser la page ouverte ou réessayer dans quelques instants.")
      this._setLoading(false)
      this._inFlight = false
      return
    }

    try {
      const resp = await fetch(this.statusUrlValue, {
        method: "GET",
        headers: { "Accept": "application/json" }
      })
      if (!resp.ok) throw new Error(await resp.text())

      const data = await resp.json()

      if (data.status === "done") {
        this._stopPolling()
        this._setCommentValue(data.ia_text || "")
        if (this.hasRunningBadgeTarget) this.runningBadgeTarget.classList.add("d-none")

        this._showSuccess()
        this._setLoading(false)
        this._inFlight = false
        return
      }

      if (data.status === "failed") {
        this._stopPolling()
        if (this.hasRunningBadgeTarget) this.runningBadgeTarget.classList.add("d-none")
        alert("La génération a échoué. Vérifie les logs (détails en console).")
        this._setLoading(false)
        this._inFlight = false
        return
      }

    } catch (e) {
      console.error(e)
      // erreur réseau ponctuelle : on continue à poll
    }

    // Backoff progressif, max 5s
    this._pollDelayMs = Math.min(5000, Math.round(this._pollDelayMs * 1.2))
    this._scheduleNextPoll(this._pollDelayMs)
  }

  _setCommentValue(value) {
    const el = this.commentFieldTarget

    if (window.jQuery && window.jQuery(el).data("summernote")) {
      window.jQuery(el).summernote("code", value)
    } else {
      el.value = value
    }

    el.dispatchEvent(new Event("input", { bubbles: true }))
  }

  _setLoading(isLoading) {
    if (isLoading && this.hasSuccessTarget) {
      this.successTarget.classList.add("d-none")
    }

    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = isLoading
    }

    if (this.hasButtonLabelTarget) {
      this.buttonLabelTarget.textContent = isLoading
        ? "Génération en cours…"
        : "Générer le commentaire IA"
    }

    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.toggle("d-none", !isLoading)
    }
  }

  _showSuccess() {
    if (!this.hasSuccessTarget) return

    const now = new Date()
    const date = now.toLocaleDateString("fr-FR")
    const time = new now.toLocaleTimeString("fr-FR")

    this.successTarget.textContent = `Commentaire généré à ${time} le ${date})`
    this.successTarget.classList.remove("d-none")
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  _showSuccess() {
    if (!this.hasSuccessTarget) return

    const now = new Date()
    const date = now.toLocaleDateString("fr-FR")
    const time = now.toLocaleTimeString("fr-FR")

    this.successTarget.textContent = `Commentaire généré le ${date} à ${time}`
    this.successTarget.classList.remove("d-none")
  }
}
